import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { User, Phone, Mail, Link as LinkIcon, Instagram } from "lucide-react";
import PlanBadge from "./PlanBadge";
import { Progress } from "@/components/ui/progress";
import { fetchCountries, fetchCounties, CountryOption, CountyOption } from "@/services/locationService";
import { useOperatorApi } from "@/hooks/useRoleBasedApi";
import { Spinner } from "@/components/ui/spinner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

interface OperatorProfileProps {
  isPremium?: boolean;
  isBasic?: boolean;
}

interface OperatorProfile {
  name: string;
  description: string;
  address: string;
  country: string;
  county: string;
  isPublic: boolean;
  phone: string;
  email: string;
  website: string;
  instagram: string;
  showPhone: boolean;
  showEmail: boolean;
  showInstagram: boolean;
}

const OperatorProfile: React.FC<OperatorProfileProps> = ({ isPremium = false, isBasic = false }) => {
  const { user } = useAuth();

  // Only allow operators to load this component
  if (!user || user.role !== "operator") {
    return (
      <div className="max-w-2xl mx-auto mt-8 p-6 text-center text-red-600 font-semibold">
        You do not have permission to view this profile.
      </div>
    );
  }

  const [profile, setProfile] = useState<OperatorProfile>({
    name: "",
    description: "",
    address: "",
    country: "",
    county: "",
    isPublic: false,
    phone: "",
    email: "",
    website: "",
    instagram: "",
    showPhone: false,
    showEmail: true,
    showInstagram: true,
  });

  // Country/county state
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [counties, setCounties] = useState<CountyOption[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [countryError, setCountryError] = useState<string | null>(null);
  const [countyError, setCountyError] = useState<string | null>(null);

  // Data loading state
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  
  const operatorApi = useOperatorApi();

  // Fetch profile data on mount
  useEffect(() => {
    const loadProfileData = async () => {
      setLoadingProfile(true);
      setProfileError(null);
      try {
        const data = await operatorApi.getProfile();
        if (data) {
          setProfile(prev => ({ ...prev, ...data }));
        }
      } catch (error) {
        console.error("Failed to load operator profile:", error);
        setProfileError("Failed to load your profile data. Please try refreshing the page.");
      } finally {
        setLoadingProfile(false);
      }
    };
    
    loadProfileData();
  }, []); // Run only once on mount

  // Fetch countries on mount
  useEffect(() => {
    setLoadingCountries(true);
    fetchCountries()
      .then((data) => {
        setCountries(data);
        setLoadingCountries(false);
      })
      .catch((err) => {
        setCountryError("Failed to load countries");
        setLoadingCountries(false);
      });
  }, []);

  // Fetch counties when country changes
  useEffect(() => {
    if (!profile.country) {
      setCounties([]);
      return;
    }
    setLoadingCounties(true);
    fetchCounties(profile.country)
      .then((data) => {
        setCounties(data);
        setLoadingCounties(false);
      })
      .catch((err) => {
        setCountyError("Failed to load counties");
        setLoadingCounties(false);
      });
  }, [profile.country]);

  // Determine user's plan
  const userPlan = isPremium ? 'premium' : 'free';

  // Calculate profile completion percentage
  const requiredFields = [profile.name, profile.description, profile.address, profile.county, profile.email, profile.phone, profile.website, profile.instagram];
  const filledFields = requiredFields.filter(Boolean).length;
  const completion = Math.round((filledFields / requiredFields.length) * 100);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value
    }));
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    setProfile((prev) => ({ ...prev, [name]: checked }));
  };

  if (loadingProfile) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="large" />
      </div>
    );
  }

  if (profileError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{profileError}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="max-w-3xl mx-auto mt-8 space-y-6">
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-sauna-navy">Complete your operator profile</h2>
          <span className="text-sm text-gray-500 font-medium">{completion}% complete</span>
        </div>
        <Progress value={completion} className="h-2" />
      </div>
      <Card className="p-6">
        <CardHeader className="pb-4">
          <CardTitle className="text-xl font-bold text-sauna-navy">Operator Profile</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <form className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2 grid gap-4">
              {/* Business Name */}
              <div>
                <div className="flex items-center mb-1.5">
                  <label className="text-sm font-semibold text-gray-800 mr-2">Business Name</label>
                  <PlanBadge requiredPlan="free" userPlan={userPlan} tooltip="Available on all plans" />
                </div>
                <Input
                  name="name"
                  placeholder="Operator Name"
                  value={profile.name}
                  onChange={handleInputChange}
                  className="focus:ring-2 focus:ring-sauna-orange"
                />
              </div>
              {/* Description */}
              <div>
                <div className="flex items-center mb-1.5">
                  <label className="text-sm font-semibold text-gray-800 mr-2">Description</label>
                  <PlanBadge requiredPlan="free" userPlan={userPlan} tooltip="Available on all plans" />
                </div>
                <Textarea
                  name="description"
                  placeholder="Description"
                  className="resize-none focus:ring-2 focus:ring-sauna-orange"
                  rows={3}
                  value={profile.description}
                  onChange={handleInputChange}
                />
              </div>
              {/* Address */}
              <div>
                <div className="flex items-center mb-1.5">
                  <label className="text-sm font-semibold text-gray-800 mr-2">Address</label>
                  <PlanBadge requiredPlan="free" userPlan={userPlan} tooltip="Available on all plans" />
                </div>
                <Input
                  name="address"
                  placeholder="Address"
                  value={profile.address}
                  onChange={handleInputChange}
                  className="focus:ring-2 focus:ring-sauna-orange"
                />
              </div>
              {/* Country & County */}
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <div className="flex items-center mb-1.5">
                    <label className="text-sm font-semibold text-gray-800 mr-2">Country</label>
                    <PlanBadge requiredPlan="free" userPlan={userPlan} tooltip="Available on all plans" />
                  </div>
                  <select
                    name="country"
                    value={profile.country}
                    onChange={handleInputChange}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background focus:ring-2 focus:ring-sauna-orange"
                    disabled={loadingCountries}
                  >
                    <option value="">{loadingCountries ? "Loading..." : "Select a country"}</option>
                    {countries.map((country) => (
                      <option key={country.value} value={country.label}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                  {countryError && <div className="text-red-500 text-xs mt-1">{countryError}</div>}
                </div>
                <div className="flex-1">
                  <div className="flex items-center mb-1.5">
                    <label className="text-sm font-semibold text-gray-800 mr-2">County</label>
                    <PlanBadge requiredPlan="free" userPlan={userPlan} tooltip="Available on all plans" />
                  </div>
                  <select
                    name="county"
                    value={profile.county}
                    onChange={handleInputChange}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background focus:ring-2 focus:ring-sauna-orange"
                    disabled={loadingCounties || !profile.country}
                  >
                    <option value="">{loadingCounties ? "Loading..." : "Select a county"}</option>
                    {counties.map((county) => (
                      <option key={county.value} value={county.value}>{county.label}</option>
                    ))}
                  </select>
                  {countyError && <div className="text-red-500 text-xs mt-1">{countyError}</div>}
                </div>
                <div className="flex items-center mb-2">
                  <Checkbox
                    id="isPublic"
                    checked={profile.isPublic}
                    onCheckedChange={(checked) => handleCheckboxChange("isPublic", checked === true)}
                  />
                  <label htmlFor="isPublic" className="ml-2 text-sm font-semibold text-gray-800">Public</label>
                </div>
              </div>
              {/* Phone & Email */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <div className="flex items-center mb-1.5">
                    <label className="text-sm font-semibold text-gray-800 mr-2">Phone Number</label>
                    <PlanBadge requiredPlan="premium" userPlan={userPlan} tooltip="Visible with Premium" />
                  </div>
                  <Input
                    name="phone"
                    placeholder="Phone Number"
                    value={profile.phone}
                    onChange={handleInputChange}
                    className="focus:ring-2 focus:ring-sauna-orange"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center mb-1.5">
                    <label className="text-sm font-semibold text-gray-800 mr-2">Email Address</label>
                    <PlanBadge requiredPlan="free" userPlan={userPlan} tooltip="Available on all plans" />
                  </div>
                  <Input
                    name="email"
                    placeholder="Email Address"
                    value={profile.email}
                    onChange={handleInputChange}
                    className="focus:ring-2 focus:ring-sauna-orange"
                  />
                </div>
              </div>
              {/* Website & Instagram */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <div className="flex items-center mb-1.5">
                    <label className="text-sm font-semibold text-gray-800 mr-2">Website URL</label>
                    <PlanBadge requiredPlan="free" userPlan={userPlan} tooltip="Available on all plans" />
                  </div>
                  <Input
                    name="website"
                    placeholder="Website URL"
                    value={profile.website}
                    onChange={handleInputChange}
                    className="focus:ring-2 focus:ring-sauna-orange"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center mb-1.5">
                    <label className="text-sm font-semibold text-gray-800 mr-2">Instagram Handle</label>
                    <PlanBadge requiredPlan="free" userPlan={userPlan} tooltip="Available on all plans" />
                  </div>
                  <Input
                    name="instagram"
                    placeholder="Instagram Handle"
                    value={profile.instagram}
                    onChange={handleInputChange}
                    className="focus:ring-2 focus:ring-sauna-orange"
                  />
                </div>
              </div>
              {/* Visibility Settings */}
              <div className="pt-2 border-t border-gray-100 mt-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-semibold text-gray-800">Visibility Settings</h3>
                </div>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="showPhone"
                      checked={profile.showPhone || isPremium}
                      onCheckedChange={(checked) => handleCheckboxChange("showPhone", checked === true)}
                      disabled={isPremium}
                    />
                    <div className="flex items-center gap-2">
                      <Label htmlFor="showPhone">Show phone number</Label>
                      {isPremium ? (
                        <PlanBadge requiredPlan="premium" userPlan={userPlan} tooltip="Always visible with Premium" />
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="showEmail"
                      checked={profile.showEmail}
                      onCheckedChange={(checked) => handleCheckboxChange("showEmail", checked === true)}
                    />
                    <Label htmlFor="showEmail">Show email</Label>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default OperatorProfile;
